// 自動旋轉設定面板 — 對照 Unity `SlotFramework/Prefab/UI/Base/AutoSpinPanel.prefab`
// ＋ `SlotFramework/Script/UI/Panel/AutoSpinPanel.cs` 逐節點還原。
//
// 先前的 `AutoSpinDialog` 是自繪 Graphics 版（繼承 ModalDialog：藍紫圓角面板＋比例式佈局），
// 與 Unity 外觀完全不同，故整檔換掉（同 BetSelectDialog → BetListPopup 的處置）。
//
// ⚠️ 縮放基準是 **1920×1080 / 1080×1920 Expand**（LandscapeUI／PoriraitUI 的 CanvasScaler），
//    **不是** `Dialog.prefab` 那個 1080×1080 取較短邊的 scaler——AutoSpinPanel 不屬於 Dialog 家族，
//    它是 `CommonPanel.prefab` 內的常駐巢狀 prefab（實例名 "AutoSpinWindow"），
//    非 Addressable 動態載入。故直接用專案既有的 unityScale* helper。
//
// 版面（prefab 序列化原值；下表座標已換算成「相對畫面中心、Unity y 向上」，PixiJS 使用時 y 取反）：
//   Mask      全螢幕 stretch、無 sprite、純黑 alpha 0.5019608、**掛 Button** ⇒ 點遮罩＝關窗
//   Bg        aPos (0,40) sizeDelta 1000×450；pic_popupBg **Sliced**（.meta spriteBorder 10/10/10/10）
//   TitleBg   錨頂滿寬、sizeDelta (0,78)、pivot 頂中、aPos (0,-1) ⇒ 中心 y=+225、寬＝Bg 寬 1000
//             pic_titleBg 原生 300×78 ⇒ 高度 1:1、只橫向拉伸；m_Type Simple（.meta 雖有 border 但未使用）
//   Title     aPos (0,0) 600×60、fontSize 36 AutoSize 18~36、Center、**scale.x 1.1**
//             白字＋TMP 垂直漸層 preset Color_Panel_Title（上 #F3E867 → 下 #F7BE31）
//   Close     aPos (380,0) 相對 TitleBg ⇒ 中心 (380,+225)、84×84、btn_close
//   5 個轉數格 TotalSpin aPos (0,60) 相對 Bg ⇒ 中心 y=+100；各格 aPos x=-320/-160/0/160/320、128×66
//             底圖 btn_autoBet、選中疊層 ActiveImage=btn_autoBet_on（全 stretch 於格內、m_RaycastTarget 0）
//             ValueText 80×50、fontSize 36 固定（AutoSize 關）、色 #FFD667；**"∞" 那格 fontSize 64**
//   勾選框    Choose aPos (-350,-40) 相對 Bg ⇒ 中心 (-350,0)；BtnChoose 58×58 btn_chooseBg
//             ChoosePoint 58×58 btn_choosePoint、**m_IsActive 0**（預設未勾選）、以顯隱切換無動畫
//   說明文字  Description aPos (-300,-40) pivot (0,0.5) ⇒ 左緣 (-300,0)；Text 700×60 pivot (0,0.5)
//             fontSize 38 AutoSize 18~38、**Left** 對齊、色 #FFF2AF
//             m_characterSpacing -4 ⇒ 字距 -4×38/100 = -1.52 設計 px；m_margin.left 8 ⇒ 左緣再 +8
//   Start     aPos (220,-150) 相對 Bg ⇒ 中心 (220,-110)、256×92、btn_start
//   Cancel    aPos (-220,-150) ⇒ 中心 (-220,-110)、256×92、btn_cancel
//   兩鈕文字  aPos (0,5) 相對鈕心（y 向上 ⇒ PixiJS -5）、160×60、fontSize 36 AutoSize 18~36、scale.x 1.1
//             白字＋漸層 preset Color_Panel_Button（上 #FDF4B4 → 下 #F7BE31）
//
// 行為（AutoSpinPanel.cs 全檔核對）：
//   - 轉數選項硬編於 `AutoSpinSetting.TotalSpinOptions = {10,25,50,100,int.MaxValue}`（本專案 AUTO_SPIN_OPTIONS，
//     int.MaxValue → Number.POSITIVE_INFINITY 顯示為 ∞）。**預設選 index 0（"10"）**
//     （`Start()` 的 `_totalSpinsToggles[0].SetIsOnWithoutNotify(true)`）。
//   - ToggleGroup `m_AllowSwitchOff: 0` ⇒ 必定恰有一格選中；選中呈現是 ActiveImage 疊層淡入
//     （Toggle `toggleTransition: 1` Fade，Unity 原生 crossfade ≈0.1s），**文字顏色/字級不變**
//     （已逐格核對 5 個 ValueText 的 m_fontColor 位元組相同）。5 格的 Selectable transition 為 None ⇒ 底圖不 tint。
//   - **Cancel／Mask／Close 三者行為完全相同**（都只是關窗，Cancel 不是「回上一步」）。
//   - 開關窗只切 CanvasGroup alpha 0/1，**無任何動畫**（不像 BetListWindow 有 0.2s DOFade/DOScale）。
//   - 選轉數與勾選都播 se_button（Se_Button）。
//   - 「觸發免費遊戲則停止」是**唯一**存在於 UI 的進階選項；AutoSpinOptionTypeEnum 的
//     StopRatio／StopLessBalance／StopMoreBalance 只是恆 0 的網路欄位，prefab 內連隱藏節點都沒有。
//
// [已知差異] ①Bg 掛 FrostedGlass 材質（`_BlurSize 5` 的 GrabPass 模糊），但 `_ImageAlpha=1` 使
//   `blurMask = 貼圖 alpha`，不透明區幾乎完全採用貼圖本色，故直接貼圖即可（同 SystemDialog／BetListPopup 的處置）。
//   ②`pic_popupBg` 的九宮格邊界本檔用 Unity `.meta` 的第一手值 **10**；既有 SystemDialog／BuyBonusDialog
//   寫 45（與 Unity 不符，改動會動到那兩個窗的外觀），屬既有差異，本輪不動。

import { Container, FillGradient, Graphics, NineSliceSprite, Sprite, Text, type TextStyleOptions } from 'pixi.js'
import { gsap } from 'gsap'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'
import { AUTO_SPIN_OPTIONS } from '../../constants'
import { fontFamilyForLocale, t } from '../../../framework/i18n/i18n'
import {
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../../framework/render/design'
import { getUiTexture } from '../../assets/UiTextures'
import { reportModalOpen } from './modalGate'

// ── 遮罩與面板 ──
/** Mask 的 m_Color.a（純黑、無 sprite）。 */
const DIM_ALPHA = 0.5019608
/** Bg aPos／sizeDelta。 */
const PANEL_AY = 40
const PANEL_W = 1000
const PANEL_H = 450
/** pic_popupBg 原生 300×300、`.meta` spriteBorder 10/10/10/10（見檔頭已知差異②）。 */
const POPUP_SLICE = 10

// ── 標題條 ──
/** TitleBg sizeDelta.y；pivot 頂中且 aPos (0,-1) ⇒ 中心 y = Bg頂(265) − 1 − 78/2。 */
const TITLE_H = 78
const TITLE_AY = PANEL_AY + PANEL_H / 2 - 1 - TITLE_H / 2
const TITLE_FONT = 36
const TITLE_FONT_MIN = 18
/** Title 的 sizeDelta.x（AutoSize 的寬度上限）。 */
const TITLE_TEXT_W = 600
/** Title／按鈕文字的 m_LocalScale.x（Unity 用橫向拉寬 1.1 倍）。 */
const TEXT_SCALE_X = 1.1

/** Close 鈕（相對 TitleBg 中心 aPos (380,0)）。 */
const CLOSE_AX = 380
const CLOSE_SIZE = 84

// ── 轉數格 ──
/** TotalSpin aPos (0,60) 相對 Bg 中心。 */
const CELL_AY = PANEL_AY + 60
/** 各 BtnTotalSpin_N 的 aPos.x（−320 起、間距 160）。 */
const CELL_AXS: readonly number[] = [-320, -160, 0, 160, 320]
const CELL_W = 128
const CELL_H = 66
/** ValueText fontSize：一般 36；"∞" 那格 prefab 覆寫為 64。 */
const CELL_FONT = 36
const CELL_FONT_INFINITE = 64
/** ValueText m_fontColor (1, 0.8376147, 0.40566033)。 */
const CELL_TEXT_COLOR = 0xffd667
/** Toggle `toggleTransition: 1`（Fade）的 Unity 原生 crossfade 時間。 */
const HIGHLIGHT_FADE_S = 0.1

// ── 勾選列 ──
/** Choose aPos (-350,-40) 相對 Bg 中心 ⇒ 絕對 y = 40 − 40 = 0。 */
const CHECK_AX = -350
const CHECK_AY = PANEL_AY - 40
const CHECK_SIZE = 58
/** Description aPos (-300,-40) pivot (0,0.5) ⇒ 文字左緣；再加 m_margin.left 8。 */
const DESC_AX = -300 + 8
const DESC_AY = CHECK_AY
const DESC_W = 700
const DESC_FONT = 38
const DESC_FONT_MIN = 18
/** Description m_fontColor (1, 0.9490197, 0.6862745)。 */
const DESC_COLOR = 0xfff2af
/** m_characterSpacing −4：TMP 單位為 fontSize 的 1/100（同 WinTemp 既有結論）。 */
const DESC_LETTER_SPACING = (-4 * DESC_FONT) / 100

// ── 開始／取消 ──
const BTN_AX = 220
const BTN_AY = PANEL_AY - 150
const BTN_W = 256
const BTN_H = 92
/** 鈕內文字 aPos (0,5)（y 向上）與 sizeDelta.x。 */
const BTN_TEXT_AY = 5
const BTN_TEXT_W = 160
const BTN_FONT = 36
const BTN_FONT_MIN = 18

/** Unity Button ColorTint 預設三態（Start／Cancel／Close／勾選框；5 個轉數格為 None 不 tint）。 */
const TINT_NORMAL = 0xffffff
const TINT_HIGHLIGHTED = 0xf5f5f5
const TINT_PRESSED = 0xc8c8c8

/** TMP 垂直漸層 preset → PixiJS FillGradient（colorMode 2＝Vertical，上下各一色）。 */
function verticalGradient(top: number, bottom: number): FillGradient {
  return new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    colorStops: [
      { offset: 0, color: top },
      { offset: 1, color: bottom },
    ],
    textureSpace: 'local',
  })
}

/** 標題字（Color_Panel_Title：上 #F3E867 → 下 #F7BE31）。 */
function titleStyle(): TextStyleOptions {
  return {
    fontFamily: fontFamilyForLocale(),
    fontSize: TITLE_FONT,
    fill: verticalGradient(0xf3e867, 0xf7be31),
    fontWeight: 'bold',
    align: 'center',
  }
}

/** 開始／取消鈕字（Color_Panel_Button：上 #FDF4B4 → 下 #F7BE31）。 */
function buttonTextStyle(): TextStyleOptions {
  return {
    fontFamily: fontFamilyForLocale(),
    fontSize: BTN_FONT,
    fill: verticalGradient(0xfdf4b4, 0xf7be31),
    fontWeight: 'bold',
    align: 'center',
  }
}

/**
 * TMP AutoSize 等價縮字（本面板的文字都是單行，故只需寬度上限）：
 * 從 max 級數起以 1pt 為步進縮到塞得進 maxW 為止，下限 min。
 * 比較時除掉 scale.x——Unity 的 autoSize 是在 TMP 自己的 local rect 內計算，之後才套 m_LocalScale。
 */
function fitWidth(text: Text, maxW: number, max: number, min: number): void {
  for (let size = max; size >= min; size -= 1) {
    text.style.fontSize = size
    if (text.width / text.scale.x <= maxW) return
  }
  text.style.fontSize = min
}

/** 依 Unity Button ColorTint 掛三態著色（本專案既有慣例，同 SystemDialog）。 */
function attachTint(target: Container, sprite: Sprite): void {
  target.eventMode = 'static'
  target.cursor = 'pointer'
  target.on('pointerover', () => (sprite.tint = TINT_HIGHLIGHTED))
  target.on('pointerout', () => (sprite.tint = TINT_NORMAL))
  target.on('pointerdown', () => (sprite.tint = TINT_PRESSED))
  target.on('pointerup', () => (sprite.tint = TINT_NORMAL))
  target.on('pointerupoutside', () => (sprite.tint = TINT_NORMAL))
}

/**
 * 一格轉數選項（BtnTotalSpin_N）。
 * 底圖 Unity 標 Sliced（border 17/20/20/17），但 sizeDelta 128×66 與 btn_autoBet 原生完全相同
 * ⇒ 不發生拉伸、Sliced 與 Simple 等價，故用普通 Sprite。
 * 選中＝ActiveImage 疊層淡入（不改文字顏色／字級，與 Unity 一致）。
 */
class AutoSpinCell extends Container {
  private readonly bg = new Sprite(getUiTexture('autoCellBg'))
  private readonly highlight = new Sprite(getUiTexture('autoCellOn'))
  // ⚠️ 不可命名為 `label`：PixiJS v8 的 Container 已內建 `label: string`（同 BetListPopup 對 `tint` 的註記）。
  private readonly caption: Text

  constructor(text: string, fontSize: number, onTap: () => void) {
    super()
    for (const sprite of [this.bg, this.highlight]) {
      sprite.anchor.set(0.5)
      sprite.setSize(CELL_W, CELL_H)
    }
    // ActiveImage 的 m_RaycastTarget 為 0；本容器統一收點擊，故疊層不參與命中測試。
    this.highlight.eventMode = 'none'
    this.highlight.alpha = 0

    this.caption = new Text({
      text,
      style: { fontFamily: fontFamilyForLocale(), fontSize, fill: CELL_TEXT_COLOR, fontWeight: 'bold' },
    })
    this.caption.anchor.set(0.5)

    this.addChild(this.bg, this.highlight, this.caption)
    this.eventMode = 'static'
    this.cursor = 'pointer'
    this.on('pointertap', onTap)
  }

  setSelected(selected: boolean): void {
    gsap.killTweensOf(this.highlight)
    gsap.to(this.highlight, { alpha: selected ? 1 : 0, duration: HIGHLIGHT_FADE_S, ease: 'none' })
  }
}

export class AutoSpinPanel extends Container implements Resizable {
  /** 全螢幕遮罩（Unity Mask：純黑 0.502＋Button ⇒ 點擊關窗）。 */
  private readonly dim = new Graphics()
  /** Unity 設計座標層（原點＝畫面中心；整層以 Expand 縮放，故子節點直接用 prefab 原值）。 */
  private readonly root = new Container()
  private readonly panelBg: NineSliceSprite
  private readonly titleBg = new Sprite(getUiTexture('titleBg'))
  private readonly titleText = new Text({ text: '', style: titleStyle() })
  private readonly closeButton = new Container()
  private readonly cells: AutoSpinCell[] = []
  private readonly checkButton = new Container()
  private readonly checkMark = new Sprite(getUiTexture('chooseMark'))
  private readonly description: Text
  private readonly startButton = new Container()
  private readonly cancelButton = new Container()

  /** 預設選 index 0（Unity Start() 的 SetIsOnWithoutNotify(true)）。 */
  private selectedIndex = 0
  /** ChoosePoint 預設 m_IsActive 0 ⇒ 未勾選。 */
  private stopOnFreeGame = false

  constructor(private readonly bus: EventBus<GameEventMap>) {
    super()
    this.visible = false
    this.eventMode = 'static'

    this.dim.eventMode = 'static'
    this.dim.on('pointertap', () => this.hide())

    this.panelBg = new NineSliceSprite({
      texture: getUiTexture('popupBg'),
      leftWidth: POPUP_SLICE,
      topHeight: POPUP_SLICE,
      rightWidth: POPUP_SLICE,
      bottomHeight: POPUP_SLICE,
    })
    this.panelBg.width = PANEL_W
    this.panelBg.height = PANEL_H
    this.panelBg.position.set(-PANEL_W / 2, -PANEL_AY - PANEL_H / 2)
    // 面板本身攔截點擊，避免冒泡到底層遮罩而誤關窗（Unity 靠 Bg 的 Image 擋 raycast）。
    this.panelBg.eventMode = 'static'

    this.titleBg.anchor.set(0.5)
    this.titleBg.setSize(PANEL_W, TITLE_H)
    this.titleBg.position.set(0, -TITLE_AY)
    this.titleText.anchor.set(0.5)
    this.titleText.scale.x = TEXT_SCALE_X
    this.titleText.position.set(0, -TITLE_AY)

    this.buildCloseButton()
    this.buildCells()
    this.buildCheckRow()
    this.description = new Text({
      text: '',
      style: {
        fontFamily: fontFamilyForLocale(),
        fontSize: DESC_FONT,
        fill: DESC_COLOR,
        fontWeight: 'bold',
        letterSpacing: DESC_LETTER_SPACING,
        align: 'left',
      },
    })
    this.description.anchor.set(0, 0.5)
    this.description.position.set(DESC_AX, -DESC_AY)

    this.buildActionButton(this.startButton, 'btnStart', BTN_AX, () => this.onStart())
    this.buildActionButton(this.cancelButton, 'btnCancel', -BTN_AX, () => this.hide())

    this.root.addChild(
      this.panelBg,
      this.titleBg,
      this.titleText,
      ...this.cells,
      this.checkButton,
      this.description,
      this.startButton,
      this.cancelButton,
      this.closeButton,
    )
    this.addChild(this.dim, this.root)

    this.bus.on('autoSpinClicked', () => this.show())
  }

  /** 右上關閉鈕（btn_close 84×84，與 Cancel／Mask 行為完全相同）。 */
  private buildCloseButton(): void {
    const cross = new Sprite(getUiTexture('btnClose'))
    cross.anchor.set(0.5)
    cross.setSize(CLOSE_SIZE, CLOSE_SIZE)
    this.closeButton.addChild(cross)
    this.closeButton.position.set(CLOSE_AX, -TITLE_AY)
    attachTint(this.closeButton, cross)
    this.closeButton.on('pointertap', () => this.hide())
  }

  /** 5 個轉數格（∞ 那格字級 64，其餘 36）。 */
  private buildCells(): void {
    AUTO_SPIN_OPTIONS.forEach((count, index) => {
      const infinite = !Number.isFinite(count)
      const cell = new AutoSpinCell(infinite ? '∞' : `${count}`, infinite ? CELL_FONT_INFINITE : CELL_FONT, () =>
        this.selectOption(index),
      )
      cell.position.set(CELL_AXS[index] ?? 0, -CELL_AY)
      this.cells.push(cell)
    })
  }

  /** 勾選框（btn_chooseBg ＋ 顯隱切換的 btn_choosePoint；Unity 為 Button＋SetActive，無動畫）。 */
  private buildCheckRow(): void {
    const box = new Sprite(getUiTexture('chooseBox'))
    box.anchor.set(0.5)
    box.setSize(CHECK_SIZE, CHECK_SIZE)
    this.checkMark.anchor.set(0.5)
    this.checkMark.setSize(CHECK_SIZE, CHECK_SIZE)
    this.checkMark.eventMode = 'none'
    this.checkMark.visible = false
    this.checkButton.addChild(box, this.checkMark)
    this.checkButton.position.set(CHECK_AX, -CHECK_AY)
    attachTint(this.checkButton, box)
    this.checkButton.on('pointertap', () => this.toggleStop())
  }

  /** 開始／取消鈕（256×92 底圖＋鈕心上方 5px 的漸層文字）。 */
  private buildActionButton(button: Container, texture: 'btnStart' | 'btnCancel', ax: number, onTap: () => void): void {
    const bg = new Sprite(getUiTexture(texture))
    bg.anchor.set(0.5)
    bg.setSize(BTN_W, BTN_H)
    const caption = new Text({ text: '', style: buttonTextStyle() })
    caption.anchor.set(0.5)
    caption.scale.x = TEXT_SCALE_X
    caption.position.set(0, -BTN_TEXT_AY)
    button.addChild(bg, caption)
    button.position.set(ax, -BTN_AY)
    attachTint(button, bg)
    button.on('pointertap', onTap)
  }

  /** 取回某鈕的文字節點（addChild 順序：[0]=底圖、[1]=文字）。 */
  private captionOf(button: Container): Text {
    return button.children[1] as Text
  }

  private selectOption(index: number): void {
    // ToggleGroup m_AllowSwitchOff 0 ⇒ 重複點同一格不會取消選取。
    this.selectedIndex = index
    this.updateSelection()
    this.bus.emit('uiButtonPressed', undefined)
  }

  private toggleStop(): void {
    this.stopOnFreeGame = !this.stopOnFreeGame
    this.checkMark.visible = this.stopOnFreeGame
    this.bus.emit('uiButtonPressed', undefined)
  }

  private updateSelection(): void {
    this.cells.forEach((cell, index) => cell.setSelected(index === this.selectedIndex))
  }

  /**
   * 開始：發 autoSpinStarted 並關窗。
   * 對應 AutoSpinPanel.OnStartButtonClick（AutoSpinStartEvent → SpinTriggerEvent，後者同時關窗）。
   */
  private onStart(): void {
    const count = AUTO_SPIN_OPTIONS[this.selectedIndex] ?? AUTO_SPIN_OPTIONS[0]!
    this.hide()
    this.bus.emit('autoSpinStarted', { count, stopOnFreeGame: this.stopOnFreeGame })
  }

  /** 開窗（Unity 只切 CanvasGroup alpha，無動畫）。選取與勾選狀態沿用上次（Unity 的 setting 是持久物件）。 */
  private show(): void {
    this.refreshTexts()
    this.updateSelection()
    this.checkMark.visible = this.stopOnFreeGame
    this.visible = true
    reportModalOpen(this, true)
  }

  private hide(): void {
    this.visible = false
    reportModalOpen(this, false)
  }

  get isOpen(): boolean {
    return this.visible
  }

  /** 套用在地化字串並依 TMP AutoSize 縮字（語系可能在開窗前才定案，故每次開窗重取）。 */
  private refreshTexts(): void {
    this.titleText.text = t('common.Auto_Setting')
    fitWidth(this.titleText, TITLE_TEXT_W, TITLE_FONT, TITLE_FONT_MIN)

    this.description.text = t('common.Auto_Stop_FG')
    fitWidth(this.description, DESC_W, DESC_FONT, DESC_FONT_MIN)

    const start = this.captionOf(this.startButton)
    start.text = t('common.Btn_Start')
    fitWidth(start, BTN_TEXT_W, BTN_FONT, BTN_FONT_MIN)

    const cancel = this.captionOf(this.cancelButton)
    cancel.text = t('common.Btn_Cancel')
    fitWidth(cancel, BTN_TEXT_W, BTN_FONT, BTN_FONT_MIN)
  }

  resize(width: number, height: number, orientation: Orientation): void {
    this.dim.clear().rect(0, 0, width, height).fill({ color: 0x000000, alpha: DIM_ALPHA })
    // Expand：橫版 reference 1920×1080、直版 1080×1920（PoriraitUI 覆寫），皆取 min(w/refW, h/refH)。
    const scale =
      orientation === 'portrait' ? unityScalePortrait(width, height) : unityScaleLandscape(width, height)
    this.root.scale.set(scale)
    this.root.position.set(width / 2, height / 2)
  }
}
